import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadFileIcon from "@mui/icons-material/UploadFileOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNewOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import StarIcon from "@mui/icons-material/StarRounded";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMoreOutlined";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLessOutlined";

// QuickScrape's "Download JSON" exports one object per product, each with a
// nested `variants` array (see the extension's popup.js downloadJson handler).
// v3.0 added product-level `category`/`description` and variant-level
// `barcode`/`weight`/`image`; v2.1 only had the fields without those. Older
// exports (v2.0) were flat: one row per variant, with capitalized column
// names. We normalize all shapes into the grouped shape below, so missing
// fields just render as blank.
function isGroupedShape(items) {
  return items.length > 0 && items.every((item) => Array.isArray(item?.variants));
}

function isFlatProductShape(items) {
  return items.length > 0 && items.some((item) => "Product" in item || "Variant" in item);
}

function normalizeGrouped(items) {
  return items.map((p) => ({
    product: p.product || "(no name)",
    brand: p.brand || "",
    category: p.category || "",
    description: p.description || "",
    currency: p.currency || "",
    rating: p.rating || "",
    reviews: p.reviews || "",
    link: p.link || "",
    images: Array.isArray(p.images) ? p.images.filter(Boolean) : [],
    source: p.source || "",
    variants: (p.variants || []).map((v) => ({
      variant: v.variant || "",
      options: v.options || "",
      sku: v.sku || "",
      barcode: v.barcode || "",
      price: v.price || "",
      was: v.was || "",
      discount: v.discount || "",
      qty: v.qty || "",
      weight: v.weight || "",
      availability: v.availability || "",
      image: v.image || "",
    })),
  }));
}

function normalizeFlat(items) {
  const byKey = new Map();
  const products = [];
  items.forEach((r) => {
    const key = `${r.Product || ""}|${r.Link || ""}`.toLowerCase();
    let p = byKey.get(key);
    if (!p) {
      p = {
        product: r.Product || "(no name)",
        brand: r.Brand || "",
        category: r.Category || "",
        description: r.Description || "",
        currency: r.Currency || "",
        rating: r.Rating || "",
        reviews: r.Reviews || "",
        link: r.Link || "",
        images: r.Images ? String(r.Images).split("|").map((s) => s.trim()).filter(Boolean) : [],
        source: r.Source || "",
        variants: [],
      };
      byKey.set(key, p);
      products.push(p);
    }
    p.variants.push({
      variant: r.Variant || "",
      options: r.Options || "",
      sku: r.SKU || "",
      barcode: r.Barcode || "",
      price: r.Price || "",
      was: r.Was || "",
      discount: r.Discount || "",
      qty: r.Qty || "",
      weight: r.Weight || "",
      availability: r.Availability || "",
      image: r["Variant Image"] || "",
    });
  });
  return products;
}

function availabilityColor(text) {
  const t = (text || "").toLowerCase();
  if (t.includes("out of stock") || t.includes("sold out")) return "error";
  if (t.includes("in stock")) return "success";
  if (t.includes("pre-order")) return "info";
  return "default";
}

function formatPrice(price, currency) {
  if (price === "" || price == null) return "—";
  return currency ? `${price} ${currency}` : String(price);
}

export default function ScrapedProducts() {
  const [products, setProducts] = useState(null);
  const [genericRows, setGenericRows] = useState(null);
  const [genericColumns, setGenericColumns] = useState([]);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(new Set());

  function handleFile(file) {
    if (!file) return;
    setError("");
    setFileName(file.name);
    setProducts(null);
    setGenericRows(null);
    setGenericColumns([]);
    setSearch("");
    setExpanded(new Set());

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        const items = Array.isArray(parsed) ? parsed : [parsed];
        if (items.length === 0) {
          setProducts([]);
          return;
        }

        if (isGroupedShape(items)) {
          setProducts(normalizeGrouped(items));
        } else if (isFlatProductShape(items)) {
          setProducts(normalizeFlat(items));
        } else {
          const keys = new Set();
          items.forEach((item) => Object.keys(item || {}).forEach((k) => keys.add(k)));
          setGenericColumns([...keys]);
          setGenericRows(items);
        }
      } catch (e) {
        setError("Could not read that file as JSON. Make sure it's the file downloaded from QuickScrape.");
      }
    };
    reader.onerror = () => setError("Could not read that file.");
    reader.readAsText(file);
  }

  const filteredProducts = useMemo(() => {
    if (!products) return null;
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) => p.product.toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q)
    );
  }, [products, search]);

  const totalVariants = useMemo(
    () => (products ? products.reduce((sum, p) => sum + p.variants.length, 0) : 0),
    [products]
  );

  function toggleExpanded(key) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function expandAll() {
    setExpanded(new Set((filteredProducts || []).map((_, i) => i)));
  }

  function collapseAll() {
    setExpanded(new Set());
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4">Scraped Products</Typography>
          <Typography variant="body2" color="text.secondary">
            Preview a products JSON file exported from the QuickScrape browser extension.
          </Typography>
        </Box>
        <Button component="label" variant="contained" startIcon={<UploadFileIcon />}>
          Upload JSON
          <input
            type="file"
            accept="application/json,.json"
            hidden
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {products === null && genericRows === null && !error && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No file loaded yet. Scrape a page with QuickScrape ("Products" extractor), click "Download JSON", then
            upload that file here.
          </Typography>
        </Paper>
      )}

      {products !== null && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`${products.length} product${products.length === 1 ? "" : "s"}`} />
              <Chip size="small" label={`${totalVariants} variant${totalVariants === 1 ? "" : "s"}`} variant="outlined" />
              {fileName && (
                <Typography variant="caption" color="text.secondary">
                  {fileName}
                </Typography>
              )}
              <Box sx={{ flexGrow: 1 }} />
              <TextField
                size="small"
                placeholder="Search by name or brand…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 240 }}
              />
              <Tooltip title="Expand all">
                <IconButton size="small" onClick={expandAll}>
                  <UnfoldMoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Collapse all">
                <IconButton size="small" onClick={collapseAll}>
                  <UnfoldLessIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Paper>

          {filteredProducts.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No products match "{search}".
            </Typography>
          )}

          <Stack spacing={1.5}>
            {filteredProducts.map((p, index) => (
              <Accordion
                key={index}
                expanded={expanded.has(index)}
                onChange={() => toggleExpanded(index)}
                disableGutters
                sx={{ "&:before": { display: "none" }, border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%", pr: 1 }}>
                    <Avatar variant="rounded" src={p.images[0]} sx={{ width: 48, height: 48, flexShrink: 0 }}>
                      <ImageOutlinedIcon />
                    </Avatar>

                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="subtitle1" noWrap title={p.product}>
                        {p.product}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        {p.brand && (
                          <Typography variant="caption" color="text.secondary">
                            {p.brand}
                          </Typography>
                        )}
                        {p.rating !== "" && (
                          <Stack direction="row" spacing={0.25} alignItems="center">
                            <StarIcon sx={{ fontSize: 14, color: "warning.main" }} />
                            <Typography variant="caption" color="text.secondary">
                              {p.rating}
                              {p.reviews ? ` (${p.reviews})` : ""}
                            </Typography>
                          </Stack>
                        )}
                        {p.category && <Chip size="small" variant="outlined" label={p.category} />}
                        {p.source && <Chip size="small" variant="outlined" label={p.source} />}
                      </Stack>
                    </Box>

                    {p.images.length > 1 && (
                      <AvatarGroup max={4} sx={{ "& .MuiAvatar-root": { width: 28, height: 28, fontSize: 12 } }}>
                        {p.images.map((src, i) => (
                          <Avatar key={i} src={src} variant="rounded" />
                        ))}
                      </AvatarGroup>
                    )}

                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={`${p.variants.length} variant${p.variants.length === 1 ? "" : "s"}`}
                    />

                    {p.link && (
                      <Tooltip title="Open source page">
                        <IconButton
                          size="small"
                          component="a"
                          href={p.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 0 }}>
                  {p.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {p.description}
                    </Typography>
                  )}

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <TableCell>Variant</TableCell>
                          <TableCell>Options</TableCell>
                          <TableCell>SKU</TableCell>
                          <TableCell>Barcode</TableCell>
                          <TableCell>Price</TableCell>
                          <TableCell>Was</TableCell>
                          <TableCell>Discount</TableCell>
                          <TableCell>Qty</TableCell>
                          <TableCell>Weight</TableCell>
                          <TableCell>Availability</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {p.variants.map((v, vi) => (
                          <TableRow key={vi} hover>
                            <TableCell padding="checkbox">
                              <Avatar variant="rounded" src={v.image || p.images[0]} sx={{ width: 32, height: 32 }}>
                                <ImageOutlinedIcon fontSize="small" />
                              </Avatar>
                            </TableCell>
                            <TableCell>{v.variant || "—"}</TableCell>
                            <TableCell>{v.options || "—"}</TableCell>
                            <TableCell>{v.sku || "—"}</TableCell>
                            <TableCell>{v.barcode || "—"}</TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>{formatPrice(v.price, p.currency)}</TableCell>
                            <TableCell sx={{ textDecoration: v.was ? "line-through" : "none", color: "text.secondary" }}>
                              {v.was ? formatPrice(v.was, p.currency) : "—"}
                            </TableCell>
                            <TableCell>
                              {v.discount ? <Chip size="small" color="error" label={`-${v.discount}`} /> : "—"}
                            </TableCell>
                            <TableCell>{v.qty !== "" ? v.qty : "—"}</TableCell>
                            <TableCell>{v.weight || "—"}</TableCell>
                            <TableCell>
                              {v.availability ? (
                                <Chip size="small" color={availabilityColor(v.availability)} label={v.availability} />
                              ) : (
                                "—"
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {p.link && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                      Source:{" "}
                      <MuiLink href={p.link} target="_blank" rel="noopener noreferrer">
                        {p.link}
                      </MuiLink>
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </>
      )}

      {genericRows !== null && (
        <Paper>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
            <Chip size="small" label={`${genericRows.length} row${genericRows.length === 1 ? "" : "s"}`} />
            {fileName && (
              <Typography variant="caption" color="text.secondary">
                {fileName}
              </Typography>
            )}
          </Stack>
          <TableContainer sx={{ maxHeight: "70vh" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {genericColumns.map((col) => (
                    <TableCell key={col}>{col}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {genericRows.map((row, index) => (
                  <TableRow key={index} hover>
                    {genericColumns.map((col) => (
                      <TableCell key={col} sx={{ maxWidth: 260 }}>
                        <Typography variant="body2" noWrap title={String(row[col] ?? "")}>
                          {String(row[col] ?? "")}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
